import { useEffect } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import {
  CreditCardBrandKeys,
  CreditCardBrand,
  PaymentOrigin,
  StatusKeys,
  FilterPaymentType,
  FilterPaymentTypeKeys,
  DateTypeFilter,
  InvoiceFilterStatus,
  InvoiceFilterType,
  RecordedValue,
  InvoiceSearchParams,
} from '@frontend/api-invoices';
import { useArrayState } from '@frontend/array';
import { formatDate, getTodaysDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { useMultiLocationUtils, usePaymentTypeMap } from '@frontend/payments-hooks';
import { paidInvoiceStatuses } from '@frontend/payments-invoice-controller';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { DropdownField, Heading, RadioField, TableFilters, useFormField } from '@frontend/design-system';
import { useProviderNameProps } from '../../../hooks/invoices/use-provider-name-props';
import { InvoiceFilterMenuProps, InvoiceRefundStatus } from '../../../types/invoice';
import { filterCardTypeMap, filterPaymentMethodMap, filterStatusMap, hasFilter } from '../../../utils/helpers';
import { PaymentTypeItem } from '../../FilterCheckbox/payment-type-item';
import { StatusItem } from '../../FilterCheckbox/status-item';
import { CardTypeFilterTitle } from '../../Shared/card-type-filter-title';
import { FilterSection } from './filter-section';

const styles = {
  overrideFilterSection: css`
    header {
      border-bottom: 0px;
    }
    #table-filters > section {
      padding: ${theme.spacing(0, 3)};
      border-top: 0px;
    }
  `,
  providerOption: css`
    span {
      display: flex;
      gap: ${theme.spacing(1)};
    }
  `,
  checkBoxStyle: css`
    div:has(> input[type='checkbox'])::after {
      border: 1px solid ${theme.colorsV2.black(35)};
    }
    margin: ${theme.spacing(1, 0)};
    > label {
      margin-left: ${theme.spacing(2)};
    }
  `,
  locationDropdown: css`
    margin-bottom: ${theme.spacing(3)};
  `,
  radioField: css`
    div:has(> input[type='radio'])::after {
      border: 1px solid ${theme.colorsV2.black(35)};
    }
    label {
      margin-left: ${theme.spacing(2)};
    }
  `,
};

const getDefaultRange = () => [
  formatDate(dayjs().startOf('day'), 'MM/DD/YYYY'),
  formatDate(dayjs().endOf('day'), 'MM/DD/YYYY'),
];

const DATA_TRACKING_PREFIX = `pay-portal-invoices-filter`;

export interface RequestTableFiltersProps extends InvoiceFilterMenuProps {
  filter: InvoiceFilterType;
  onChangeFilter: (filter: InvoiceFilterType) => void;
  resetCurrentPage: () => void;
  setOrder: (order: InvoiceSearchParams['order']) => void;
}

export const RequestTableFilters = ({
  modalProps,
  filter,
  setOrder,
  setShowNotification,
  onChangeFilter,
  resetCurrentPage,
}: RequestTableFiltersProps) => {
  const { t } = useTranslation('payments');
  const { selectedLocationIds: locationIds } = useAppScopeStore();
  const { getLocationName } = useMultiLocationUtils();
  const { paymentTypeMap } = usePaymentTypeMap();

  useEffect(() => {
    setShowNotification(hasFilter(filter, ['dateRange']));
  }, [filter]);

  const handleOnApply = (newFilter: InvoiceFilterType) => {
    setShowNotification(hasFilter(newFilter, ['dateRange']));
    onChangeFilter(newFilter);
  };

  // DATE OPTIONS
  const initialDates = filter?.dateRange;
  const initialDateType = filter?.dateType;

  const dateRangeProps = useFormField({
    type: 'dateRange',
    value: initialDates?.start && initialDates?.end ? [initialDates.start, initialDates.end] : getDefaultRange(),
    maxDate: getTodaysDate('YYYY-M-D'),
  });

  const { providerLocationProps, providerNameProps, providerLocation, providers, getProviderName } =
    useProviderNameProps({ filter });

  // DATE TYPE
  const dateTypeProps = useFormField({
    type: 'radio',
    value: initialDateType ?? '',
  });

  // PAYMENT TYPE
  const [paymentType, setPaymentType, addPaymentType, removePaymentType] = useArrayState<PaymentOrigin>(
    filter?.paymentType ?? [],
    [filter?.paymentType]
  );

  const [paymentMethod, setPaymentMethod, addPaymentMethod, removePaymentMethod] = useArrayState<FilterPaymentType>(
    filter?.paymentMethod ?? [],
    [filter?.paymentMethod]
  );

  // CARD TYPE
  const [cardType, setCardType, addCardType, removeCardType] = useArrayState<CreditCardBrand>(filter?.cardType ?? [], [
    filter?.cardType,
  ]);

  // STATUS OPTIONS
  const [status, setStatus, addStatus, removeStatus] = useArrayState<InvoiceFilterStatus | InvoiceRefundStatus>(
    filter?.status ?? [],
    [filter?.status]
  );

  // STATUS FILTER LOGIC
  // - shows paid/cancel when date paid filter is used

  // state variable so we know the date filter is opened or closed

  const disableStatusFilter = (status: InvoiceFilterStatus) =>
    dateTypeProps.touched &&
    dateTypeProps.value === DateTypeFilter.Paid &&
    (status === InvoiceFilterStatus.unpaid || status === InvoiceFilterStatus.canceled);

  useEffect(() => {
    if (dateTypeProps.touched && dateTypeProps.value === DateTypeFilter.Paid) {
      setStatus(status.filter((item) => item !== InvoiceFilterStatus.canceled && item !== InvoiceFilterStatus.unpaid));
      if (!status.length) {
        setStatus((prevState) => [...prevState, InvoiceFilterStatus.paid, InvoiceFilterStatus.partiallyPaid]);
      }
    }
  }, [dateRangeProps.value, dateTypeProps.value, dateTypeProps.touched]);

  // RECORDED FILTER
  const recordedStatusProps = useFormField({
    type: 'radio',
    value: filter?.recorded ?? '',
  });

  // Build filter

  const handleApply = () => {
    const newFilter: InvoiceFilterType = { dateRange: filter?.dateRange };
    if (!status.length && dateTypeProps.touched && dateTypeProps.value === DateTypeFilter.Paid) {
      newFilter.status = [...status, ...paidInvoiceStatuses];
    }

    if (status.length > 0) {
      newFilter.status = status;
    }

    if (dateTypeProps.touched) {
      newFilter.dateType = dateTypeProps.value as DateTypeFilter;
      if (newFilter.dateType) {
        setOrder(dateTypeProps.value === DateTypeFilter.Paid ? '-paidAt' : '-billedAt');
        resetCurrentPage();
      }
    }

    if (recordedStatusProps.value) {
      newFilter.recorded = recordedStatusProps.value as RecordedValue;
    }

    if (paymentType.length > 0) {
      newFilter.paymentType = paymentType;
    }
    if (paymentMethod.length > 0) {
      newFilter.paymentMethod = paymentMethod;
    }

    if (cardType.length > 0) {
      if (!newFilter.paymentMethod?.includes(FilterPaymentType.Card))
        newFilter.paymentMethod = [...(newFilter.paymentMethod ?? []), FilterPaymentType.Card];
      newFilter.cardType = cardType;
    }

    if (providerLocation && providerNameProps.value) {
      const selectedProvider = providers.find((provider) => provider.id === providerNameProps.value);
      if (selectedProvider) {
        newFilter.providerLocation = providerLocation;
        newFilter.providerName = getProviderName(selectedProvider);
      }
    }

    handleOnApply(newFilter);
  };

  const onClearProviderName = () => {
    providerNameProps.onChange({ value: '', name: '' });
    if (locationIds.length > 1) providerLocationProps.onChange({ value: '', name: '' });
  };

  const onClearDateType = () => {
    dateTypeProps.onChange({ value: '', name: '' });
    setStatus([]);
  };

  const onClearStatus = () => setStatus([]);

  const onClearRecordedStatus = () => recordedStatusProps.onChange({ value: '', name: '' });

  const onClearCollectionMethod = () => setPaymentType([]);

  const onClearPaymentMethod = () => setPaymentMethod([]);

  const onClearCardType = () => setCardType([]);

  const handleClearAll = () => {
    onClearProviderName();
    onClearDateType();
    onClearRecordedStatus();
    onClearCollectionMethod();
    onClearPaymentMethod();
    onClearCardType();
    onChangeFilter({});
    modalProps.onClose();
  };

  return (
    <TableFilters
      {...modalProps}
      trackingId={DATA_TRACKING_PREFIX}
      onApplyClick={handleApply}
      onClearAllClick={handleClearAll}
      headerLabel={t('Filters')}
      css={styles.overrideFilterSection}
    >
      <FilterSection
        sectionHeaderLabel={<Heading level={3}>{t('Provider Name')}</Heading>}
        disableClear={(locationIds.length === 1 || !providerLocation) && !providerNameProps.value}
        onClearClick={onClearProviderName}
        css={styles.checkBoxStyle}
        data-trackingid={`${DATA_TRACKING_PREFIX}-cbx-provider-name`}
      >
        {locationIds.length > 1 && (
          <DropdownField
            name='location-name-filter'
            label={t('Location')}
            {...providerLocationProps}
            css={styles.locationDropdown}
            data-trackingid={`${DATA_TRACKING_PREFIX}-drp-location-name`}
          >
            {locationIds.map((location) => {
              return (
                <DropdownField.Option
                  value={location}
                  data-trackingid={`${DATA_TRACKING_PREFIX}-drp-${location}`}
                  key={location}
                >
                  {getLocationName(location)}
                </DropdownField.Option>
              );
            })}
          </DropdownField>
        )}
        <DropdownField
          name='provider-name-filter'
          label={t('Provider')}
          {...providerNameProps}
          disabled={!providerLocationProps.value}
          data-trackingid={`${DATA_TRACKING_PREFIX}-drp-provider-name`}
        >
          {providers.map((provider) => {
            const providerName = getProviderName(provider);
            return (
              <DropdownField.Option
                value={provider.id ?? ''}
                data-trackingid={`${DATA_TRACKING_PREFIX}-drp-${providerName}`}
                key={provider.id}
              >
                {providerName}
              </DropdownField.Option>
            );
          })}
        </DropdownField>
      </FilterSection>
      <FilterSection
        sectionHeaderLabel={<Heading level={3}>{t('Date Type')}</Heading>}
        disableClear={dateTypeProps.value === ''}
        onClearClick={onClearDateType}
        data-trackingid={`${DATA_TRACKING_PREFIX}-cbx-datetype-by`}
      >
        <RadioField
          name='date-type'
          {...dateTypeProps}
          css={styles.radioField}
          data-trackingid={`${DATA_TRACKING_PREFIX}-drp-datetype`}
        >
          <RadioField.Radio value={DateTypeFilter.Paid} data-trackingid={`${DATA_TRACKING_PREFIX}-drp-item-paid`}>
            {t('Date Invoice Paid')}
          </RadioField.Radio>
          <RadioField.Radio value={DateTypeFilter.Created} data-trackingid={`${DATA_TRACKING_PREFIX}-drp-item-created`}>
            {t('Date Invoice Created')}
          </RadioField.Radio>
        </RadioField>
      </FilterSection>
      <FilterSection
        sectionHeaderLabel={<Heading level={3}>{t('Payment Request Status')}</Heading>}
        disableClear={!status.length}
        onClearClick={onClearStatus}
        data-trackingid={`${DATA_TRACKING_PREFIX}-cbx-status`}
      >
        {Object.entries(InvoiceFilterStatus).map(([statusKey, statusValue]) => {
          return statusValue === 'CANCELED' ? null : (
            <StatusItem<InvoiceFilterStatus | InvoiceRefundStatus>
              key={statusValue}
              value={statusValue}
              label={filterStatusMap(statusKey as StatusKeys)}
              status={status}
              onAdd={addStatus}
              onRemove={removeStatus}
              css={styles.checkBoxStyle}
              disabled={disableStatusFilter(statusValue)}
              data-trackingid={`${DATA_TRACKING_PREFIX}-cbx-${statusKey.toLowerCase()}`}
            />
          );
        })}
      </FilterSection>
      <FilterSection
        sectionHeaderLabel={<Heading level={3}>{t('Recorded Status')}</Heading>}
        disableClear={!recordedStatusProps.value}
        onClearClick={onClearRecordedStatus}
        data-trackingid={`${DATA_TRACKING_PREFIX}-cbx-recorded`}
      >
        <RadioField
          name='recorded'
          {...recordedStatusProps}
          css={styles.radioField}
          data-trackingid={`${DATA_TRACKING_PREFIX}-drp-recorded`}
        >
          <RadioField.Radio value={RecordedValue.recorded} data-trackingid={`${DATA_TRACKING_PREFIX}-rdo-recorded`}>
            {t('Recorded')}
          </RadioField.Radio>
          <RadioField.Radio
            value={RecordedValue.notRecorded}
            data-trackingid={`${DATA_TRACKING_PREFIX}-rdo-notrecorded`}
          >
            {t('Not Recorded')}
          </RadioField.Radio>
        </RadioField>
      </FilterSection>
      <FilterSection
        sectionHeaderLabel={<Heading level={3}>{t('Collection Method')}</Heading>}
        disableClear={!paymentType.length}
        onClearClick={onClearCollectionMethod}
        data-trackingid={`${DATA_TRACKING_PREFIX}-cbx-payment-type`}
      >
        {Object.entries(paymentTypeMap).map(([typeKey, typeValue]) => {
          return (
            <PaymentTypeItem
              key={typeKey}
              value={typeKey as PaymentOrigin}
              label={typeValue || ''}
              paymentType={paymentType}
              onAdd={addPaymentType}
              onRemove={removePaymentType}
              css={styles.checkBoxStyle}
              disabled={false}
              data-trackingid={`${DATA_TRACKING_PREFIX}-cbx-payment-type-${typeKey.toLowerCase()}`}
            />
          );
        })}
      </FilterSection>
      <FilterSection
        sectionHeaderLabel={<Heading level={3}>{t('Payment Method')}</Heading>}
        disableClear={!paymentMethod.length}
        onClearClick={onClearPaymentMethod}
        data-trackingid={`${DATA_TRACKING_PREFIX}-cbx-payment-method`}
      >
        {Object.entries(FilterPaymentType).map(([paymentMethodKey, paymentMethodValue]) => (
          <StatusItem<FilterPaymentType>
            key={paymentMethodValue}
            value={paymentMethodValue}
            label={filterPaymentMethodMap(paymentMethodKey as FilterPaymentTypeKeys)}
            status={paymentMethod}
            onAdd={addPaymentMethod}
            onRemove={removePaymentMethod}
            css={styles.checkBoxStyle}
            disabled={false}
            data-trackingid={`${DATA_TRACKING_PREFIX}-cbx-${paymentMethodKey.toLowerCase()}`}
          />
        ))}
      </FilterSection>
      <FilterSection
        sectionHeaderLabel={
          <Heading level={3}>
            <CardTypeFilterTitle />
          </Heading>
        }
        disableClear={!cardType.length}
        onClearClick={onClearCardType}
        data-trackingid={`${DATA_TRACKING_PREFIX}-cbx-card-type`}
      >
        {Object.entries(CreditCardBrand).map(([cardTypeKey, cardTypeValue]) => (
          <StatusItem<CreditCardBrand>
            key={cardTypeValue}
            value={cardTypeValue}
            label={filterCardTypeMap(cardTypeKey as CreditCardBrandKeys)}
            status={cardType}
            onAdd={addCardType}
            onRemove={removeCardType}
            css={styles.checkBoxStyle}
            disabled={false}
            data-trackingid={`${DATA_TRACKING_PREFIX}-cbx-${cardTypeKey.toLowerCase()}`}
          />
        ))}
      </FilterSection>
    </TableFilters>
  );
};
